/**
 * Open-Meteo weather client — v2 (2026-05-17)
 *
 * Production-grade implementation supporting:
 *   - Single-location current conditions
 *   - Multi-waypoint hourly forecasts along a route
 *   - Per-segment temperature assignment (engine takes ambient_c as REQUIRED
 *     input; we sample 3-5 waypoints and interpolate)
 *   - In-memory caching (1-hour TTL) to avoid hammering the free tier
 *   - Retry with backoff for transient failures
 *   - Graceful fallback when the API is unreachable
 *
 * Docs: https://open-meteo.com/en/docs
 * License: free for non-commercial use; commercial use requires paid plan.
 *
 * Response shape (verified live 2026-05-17):
 *   - Single lat/lng → returns a dict
 *   - Multi lat/lng (comma-separated) → returns an array of dicts
 *   - timezone=auto returns times in LOCAL time, NOT UTC.
 */

const BASE = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_OPEN_METEO_BASE)
  || 'https://api.open-meteo.com/v1';

const FETCH_TIMEOUT_MS = 8000;
const MAX_RETRIES = 2;
const RETRY_BACKOFF_MS = 500;
const CACHE_TTL_MS = 60 * 60 * 1000;   // 1 hour
const MAX_WAYPOINTS_PER_REQUEST = 100; // we batch beyond this

// ============================================================================
// IN-MEMORY CACHE — survives across a single page load / request lifecycle.
// Keyed by stringified params. Stale entries are evicted on lookup.
// ============================================================================
const cache = new Map();

function cacheGet(key) {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.t > CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return entry.v;
}

function cacheSet(key, value) {
  cache.set(key, { t: Date.now(), v: value });
}

// ============================================================================
// FETCH WITH TIMEOUT + RETRY
// ============================================================================
async function fetchWithRetry(url, opts = {}, attempt = 0) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const resp = await fetch(url, { ...opts, signal: controller.signal });
    clearTimeout(timer);
    if (!resp.ok) {
      // 4xx errors (bad request, etc) don't benefit from retry
      if (resp.status >= 400 && resp.status < 500) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Open-Meteo ${resp.status}: ${text || resp.statusText}`);
      }
      // 5xx and network errors retry
      throw new Error(`Open-Meteo transient ${resp.status}`);
    }
    return resp.json();
  } catch (err) {
    clearTimeout(timer);
    const isAbort = err.name === 'AbortError';
    const isTransient = isAbort || err.message.includes('transient');
    if (isTransient && attempt < MAX_RETRIES) {
      await sleep(RETRY_BACKOFF_MS * Math.pow(2, attempt));
      return fetchWithRetry(url, opts, attempt + 1);
    }
    throw err;
  }
}

function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

// ============================================================================
// PUBLIC: getCurrentWeather — single location, NOW
// ============================================================================
/**
 * Get current weather at a single location. Used for the trip-input screen's
 * "current conditions" display.
 *
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<{ambient_c, wind_speed_kmh, wind_direction_deg, source, observed_at}>}
 */
export async function getCurrentWeather(lat, lng) {
  const key = `current:${lat.toFixed(3)},${lng.toFixed(3)}`;
  const cached = cacheGet(key);
  if (cached) return cached;

  const url = new URL(`${BASE}/forecast`);
  url.searchParams.set('latitude', lat.toFixed(4));
  url.searchParams.set('longitude', lng.toFixed(4));
  url.searchParams.set('current', 'temperature_2m,wind_speed_10m,wind_direction_10m');

  let data;
  try {
    data = await fetchWithRetry(url.toString());
  } catch (err) {
    // Fallback when API unreachable — return null result so the engine
    // can decide what to do. Caller should NOT use this for serious predictions.
    return {
      ambient_c: null,
      wind_speed_kmh: null,
      wind_direction_deg: null,
      source: 'unreachable',
      observed_at: null,
      error: err.message
    };
  }

  const result = {
    ambient_c: data.current?.temperature_2m ?? null,
    wind_speed_kmh: data.current?.wind_speed_10m ?? null,
    wind_direction_deg: data.current?.wind_direction_10m ?? null,
    source: 'open-meteo',
    observed_at: data.current?.time ?? null
  };
  cacheSet(key, result);
  return result;
}

// ============================================================================
// PUBLIC: getWeatherAtWaypoints — multi-location, specific time
// ============================================================================
/**
 * Get hourly forecast at a list of waypoints for the chosen departure time.
 * Returns one entry per waypoint, in the same order as input.
 *
 * @param {Array<{lat: number, lng: number}>} waypoints
 * @param {Date} [departureTime] defaults to NOW
 * @returns {Promise<Array<{lat, lng, ambient_c, wind_speed_kmh, wind_direction_deg, observed_at, source}>>}
 */
export async function getWeatherAtWaypoints(waypoints, departureTime = new Date()) {
  if (!waypoints || waypoints.length === 0) return [];

  // Cache by waypoint set + target hour
  const targetHour = floorToHour(departureTime);
  const key = `waypoints:${targetHour.toISOString()}:${waypoints.map(w => `${w.lat.toFixed(3)},${w.lng.toFixed(3)}`).join('|')}`;
  const cached = cacheGet(key);
  if (cached) return cached;

  // Batch requests if needed (>100 points)
  if (waypoints.length > MAX_WAYPOINTS_PER_REQUEST) {
    const batches = [];
    for (let i = 0; i < waypoints.length; i += MAX_WAYPOINTS_PER_REQUEST) {
      batches.push(waypoints.slice(i, i + MAX_WAYPOINTS_PER_REQUEST));
    }
    const batchResults = await Promise.all(
      batches.map(batch => getWeatherAtWaypoints(batch, departureTime))
    );
    const flattened = batchResults.flat();
    cacheSet(key, flattened);
    return flattened;
  }

  const lats = waypoints.map(w => w.lat.toFixed(4)).join(',');
  const lngs = waypoints.map(w => w.lng.toFixed(4)).join(',');

  const url = new URL(`${BASE}/forecast`);
  url.searchParams.set('latitude', lats);
  url.searchParams.set('longitude', lngs);
  url.searchParams.set('hourly', 'temperature_2m,wind_speed_10m,wind_direction_10m');
  url.searchParams.set('timezone', 'auto');
  url.searchParams.set('forecast_days', '2');

  let data;
  try {
    data = await fetchWithRetry(url.toString());
  } catch (err) {
    // Fallback: return null entries; engine should reject or warn upstream
    return waypoints.map(wp => ({
      lat: wp.lat, lng: wp.lng,
      ambient_c: null, wind_speed_kmh: null, wind_direction_deg: null,
      observed_at: null, source: 'unreachable', error: err.message
    }));
  }

  // Open-Meteo returns: array of dicts (multi-location) OR single dict
  const results = Array.isArray(data) ? data : [data];

  // For each waypoint, find the hour index closest to target.
  const out = waypoints.map((wp, i) => {
    const r = results[i];
    if (!r?.hourly?.time) {
      return {
        lat: wp.lat, lng: wp.lng,
        ambient_c: null, wind_speed_kmh: null, wind_direction_deg: null,
        observed_at: null, source: 'no-data-for-point'
      };
    }

    const idx = pickClosestHourIndex(r.hourly.time, departureTime, r.utc_offset_seconds);

    return {
      lat: wp.lat, lng: wp.lng,
      ambient_c: r.hourly.temperature_2m?.[idx] ?? null,
      wind_speed_kmh: r.hourly.wind_speed_10m?.[idx] ?? null,
      wind_direction_deg: r.hourly.wind_direction_10m?.[idx] ?? null,
      observed_at: r.hourly.time[idx],
      source: 'open-meteo'
    };
  });

  cacheSet(key, out);
  return out;
}

// ============================================================================
// PUBLIC: pickRouteSampleWaypoints — pick representative points along route
// ============================================================================
/**
 * Given a route's segments, pick `n` evenly-spaced sample waypoints for
 * weather sampling. For a 200 km route with n=4 we'd sample at km 0, 67,
 * 133, 200. This keeps API calls cheap while capturing temperature gradient
 * along the route (e.g. Mumbai 31°C → Lonavala 27°C → Pune 36°C).
 *
 * @param {{segments: Array<{lat, lng, distance_km}>}} route
 * @param {number} [n=4] how many samples (3-5 is the sweet spot)
 * @returns {Array<{lat, lng, km_in: number}>}
 */
export function pickRouteSampleWaypoints(route, n = 4) {
  if (!route?.segments?.length) return [];
  if (n < 2) n = 2;

  // Compute cumulative distance per segment START
  let total = 0;
  const cumulative = route.segments.map(s => {
    const start = total;
    total += s.distance_km;
    return start;
  });
  const totalKm = total;

  // Pick n target km values evenly along the route
  const targets = [];
  for (let i = 0; i < n; i++) {
    targets.push((i / (n - 1)) * totalKm);
  }

  // For each target, find the segment whose start km is closest
  return targets.map(target_km => {
    let bestIdx = 0;
    let bestDelta = Infinity;
    for (let i = 0; i < cumulative.length; i++) {
      const delta = Math.abs(cumulative[i] - target_km);
      if (delta < bestDelta) {
        bestDelta = delta;
        bestIdx = i;
      }
    }
    return {
      lat: route.segments[bestIdx].lat,
      lng: route.segments[bestIdx].lng,
      km_in: cumulative[bestIdx]
    };
  });
}

// ============================================================================
// PUBLIC: interpolateSegmentWeather — segment-by-segment interpolation
// ============================================================================
/**
 * Given route segments and weather sampled at N waypoints, produce an array
 * with one weather entry per segment, linearly interpolated by km_in.
 *
 * Wind direction uses nearest-sample (circular interpolation is fiddly and
 * the engine projects on bearing anyway, so a ~15° error is acceptable).
 *
 * @param {{segments: Array<{distance_km}>}} route
 * @param {Array<{km_in, ambient_c, wind_speed_kmh, wind_direction_deg}>} sampledWeather
 * @returns {Array<{ambient_c, wind_speed_kmh, wind_direction_deg, source}>}
 */
export function interpolateSegmentWeather(route, sampledWeather) {
  if (!route?.segments?.length) return [];
  if (!sampledWeather?.length) {
    return route.segments.map(() => ({
      ambient_c: null,
      wind_speed_kmh: null,
      wind_direction_deg: null,
      source: 'no-samples'
    }));
  }

  // Sort samples by km_in (defensive)
  const samples = [...sampledWeather].sort((a, b) => (a.km_in ?? 0) - (b.km_in ?? 0));

  // Build cumulative km at each segment START
  let cumulative_km = 0;
  return route.segments.map(seg => {
    const seg_start_km = cumulative_km;
    cumulative_km += seg.distance_km;
    const seg_mid_km = seg_start_km + seg.distance_km / 2;

    // Find bracketing samples
    let before = samples[0];
    let after = samples[samples.length - 1];
    for (let i = 0; i < samples.length - 1; i++) {
      if (samples[i].km_in <= seg_mid_km && samples[i + 1].km_in >= seg_mid_km) {
        before = samples[i];
        after = samples[i + 1];
        break;
      }
    }

    if (before === after || before.km_in === after.km_in) {
      return {
        ambient_c: before.ambient_c,
        wind_speed_kmh: before.wind_speed_kmh,
        wind_direction_deg: before.wind_direction_deg,
        source: before.source ?? 'open-meteo-nearest'
      };
    }

    const range = after.km_in - before.km_in;
    const frac = Math.max(0, Math.min(1, (seg_mid_km - before.km_in) / range));

    return {
      ambient_c: lerp(before.ambient_c, after.ambient_c, frac),
      wind_speed_kmh: lerp(before.wind_speed_kmh, after.wind_speed_kmh, frac),
      wind_direction_deg: frac < 0.5 ? before.wind_direction_deg : after.wind_direction_deg,
      source: 'open-meteo-interpolated'
    };
  });
}

// ============================================================================
// PUBLIC: getWeatherAlongRoute — one-shot convenience function
// ============================================================================
/**
 * Given a route, sample N waypoints, fetch weather for them, and return
 * per-segment weather entries ready for planSoCAlongRoute().
 *
 * @param {{segments: Array<{lat, lng, distance_km}>}} route
 * @param {Object} [opts]
 * @param {number} [opts.sample_count=4] number of waypoints to sample (3-5)
 * @param {Date}   [opts.departureTime=now]
 * @returns {Promise<Array<{ambient_c, wind_speed_kmh, wind_direction_deg, source}>>}
 */
export async function getWeatherAlongRoute(route, opts = {}) {
  const { sample_count = 4, departureTime = new Date() } = opts;

  const samples = pickRouteSampleWaypoints(route, sample_count);
  if (samples.length === 0) return [];

  const sampledWeather = await getWeatherAtWaypoints(samples, departureTime);

  // Zip km_in back onto weather records (getWeatherAtWaypoints preserves order)
  const sampledWithKm = sampledWeather.map((w, i) => ({
    ...w,
    km_in: samples[i].km_in
  }));

  return interpolateSegmentWeather(route, sampledWithKm);
}

// ============================================================================
// INTERNAL HELPERS
// ============================================================================

/**
 * Floor a Date to the start of the hour.
 */
function floorToHour(d) {
  const x = new Date(d);
  x.setMinutes(0, 0, 0);
  return x;
}

/**
 * Pick the index in an Open-Meteo `hourly.time` array closest to the target.
 *
 * Open-Meteo with timezone=auto returns LOCAL time strings like
 * "2026-05-17T13:00" (no timezone suffix). We convert to a comparable
 * timestamp by parsing as if it were UTC, then applying the response's
 * utc_offset_seconds.
 */
function pickClosestHourIndex(times, target, utc_offset_seconds) {
  if (!times?.length) return 0;
  const targetMs = target.getTime();
  let bestIdx = 0;
  let bestDelta = Infinity;
  for (let i = 0; i < times.length; i++) {
    // times[i] is local time at the queried location; convert to UTC ms
    const localMs = new Date(times[i] + 'Z').getTime();
    const trueUtcMs = localMs - (utc_offset_seconds ?? 0) * 1000;
    const delta = Math.abs(trueUtcMs - targetMs);
    if (delta < bestDelta) {
      bestDelta = delta;
      bestIdx = i;
    }
  }
  return bestIdx;
}

function lerp(a, b, t) {
  if (a == null || b == null) return a ?? b;
  return a + (b - a) * t;
}

// ============================================================================
// TESTING HOOK — clear cache (used by tests)
// ============================================================================
export function _clearCache_testOnly() {
  cache.clear();
}
